import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Quote, ArrowRight, ArrowLeft, LayoutGrid } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Reveal } from "@/components/Reveal";
import { homeTestimonials } from "@/data/homeTestimonials";

// Respect the visitor's reduced-motion preference for all interaction animations.
const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
};

const num = (i) => String(i + 1).padStart(2, "0");

// One reusable testimonial card — used in both the carousel and the "View All" grid.
const TestimonialCard = ({ t, index, onOpen }) => (
  <div className="group h-full flex flex-col bg-ink-base border border-white/10 p-7 md:p-8 transition-colors duration-300 hover:border-burnt/40">
    <div className="flex items-center gap-4">
      <span className="font-mono text-sm text-burnt">{num(index)}</span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
    <Quote className="mt-5 text-[#C85A17]/40" size={30} strokeWidth={1.5} aria-hidden="true" />
    <p className="mt-3 text-[#D6D6D6] leading-relaxed line-clamp-4 flex-1">
      {t.preview[0]}
    </p>
    <div className="mt-7 pt-5 border-t border-white/5 flex items-end justify-between gap-3">
      <div className="min-w-0">
        <p className="font-heading text-[#F5F3EE] font-semibold truncate">{t.name}</p>
        <p className="mt-1 text-xs font-medium text-[#D9822B] leading-snug">{t.relation}</p>
      </div>
      <button
        type="button"
        onClick={() => onOpen(index)}
        data-testid={`testimonial-read-${t.id}`}
        aria-label={`Read the full testimonial from ${t.name}`}
        className="group/btn inline-flex items-center gap-1.5 text-burnt font-medium text-sm shrink-0 transition-colors hover:text-burnt-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base"
      >
        Read Full Story
        <ArrowRight size={14} className="transition-transform group-hover/btn:translate-x-1" />
      </button>
    </div>
  </div>
);

export const Testimonials = () => {
  const reduced = usePrefersReducedMotion();
  const featuredIndex = homeTestimonials.findIndex((t) => t.featured);
  const featured = homeTestimonials[featuredIndex] ?? homeTestimonials[0];
  // Carousel = everyone except the featured testimonial (keep original order).
  const rest = homeTestimonials
    .map((t, i) => ({ t, i }))
    .filter(({ i }) => i !== featuredIndex);

  const [showAll, setShowAll] = useState(false);
  const [modalIndex, setModalIndex] = useState(null); // global index into homeTestimonials

  // ── Embla carousel ──────────────────────────────────────────────
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: false,
    slidesToScroll: 1,
    duration: reduced ? 0 : 22,
  });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const onSelect = useCallback((api) => {
    setSelected(api.selectedScrollSnap());
    setCanPrev(api.canScrollPrev());
    setCanNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    onSelect(emblaApi);
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", (api) => {
      setSnaps(api.scrollSnapList());
      onSelect(api);
    });
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, onSelect, showAll]);

  // ── Modal navigation across ALL testimonials ────────────────────
  const openModal = useCallback((globalIndex) => setModalIndex(globalIndex), []);
  const closeModal = useCallback(() => setModalIndex(null), []);
  const modalPrev = useCallback(
    () => setModalIndex((i) => (i > 0 ? i - 1 : i)),
    []
  );
  const modalNext = useCallback(
    () => setModalIndex((i) => (i < homeTestimonials.length - 1 ? i + 1 : i)),
    []
  );

  // Pause Lenis smooth scroll while the modal is open (always resumes on cleanup).
  useEffect(() => {
    const lenis = window.__lenis;
    if (!lenis) return;
    if (modalIndex !== null) lenis.stop();
    else lenis.start();
    return () => window.__lenis?.start();
  }, [modalIndex]);

  // Arrow-key navigation inside the modal.
  useEffect(() => {
    if (modalIndex === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") modalPrev();
      else if (e.key === "ArrowRight") modalNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalIndex, modalPrev, modalNext]);

  const active = modalIndex !== null ? homeTestimonials[modalIndex] : null;

  return (
    <section
      className="py-24 md:py-36 bg-ink-surface border-y border-white/10"
      data-testid="home-testimonials"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* ── Header ── */}
        <Reveal>
          <p className="label-eyebrow mb-4">Testimonials</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
            In Their Words
          </h2>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            Perspectives from the people I&apos;ve had the opportunity to coach,
            work with, and learn from through basketball.
          </p>
        </Reveal>

        {/* ── Featured (Rita Z.) ── */}
        <Reveal className="mt-12 md:mt-16">
          <div className="relative border border-white/10 bg-ink-base p-8 md:p-12" data-testid="testimonial-featured">
            <div className="flex items-center gap-4">
              <span className="font-mono text-sm text-burnt">{num(featuredIndex)}</span>
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#D9822B] font-medium">Featured</span>
            </div>
            <Quote className="mt-6 text-[#C85A17]/40" size={44} strokeWidth={1.5} aria-hidden="true" />
            <p className="mt-4 font-heading text-xl md:text-2xl font-light leading-relaxed text-[#F5F3EE] text-balance max-w-3xl">
              {featured.preview[0]}
            </p>
            <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-heading text-lg text-[#F5F3EE] font-semibold">{featured.name}</p>
                <p className="mt-1 text-sm font-medium text-[#D9822B]">{featured.relation}</p>
              </div>
              <button
                type="button"
                onClick={() => openModal(featuredIndex)}
                data-testid={`testimonial-read-${featured.id}`}
                aria-label={`Read the full testimonial from ${featured.name}`}
                className="group/btn inline-flex items-center gap-2 text-burnt font-medium text-sm transition-colors hover:text-burnt-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base"
              >
                Read Full Story
                <ArrowRight size={14} className="transition-transform group-hover/btn:translate-x-1" />
              </button>
            </div>
          </div>
        </Reveal>

        {/* ── Carousel OR grid ── */}
        {!showAll ? (
          <div className="mt-6" data-testid="testimonials-carousel">
            {/* Controls row */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C85A17]">
                More Voices
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => emblaApi?.scrollPrev()}
                  disabled={!canPrev}
                  aria-label="Previous testimonials"
                  data-testid="carousel-prev"
                  className="h-9 w-9 grid place-items-center border border-white/15 text-white transition-colors hover:border-burnt hover:text-burnt disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-white/15 disabled:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt focus-visible:ring-offset-2 focus-visible:ring-offset-ink-surface"
                >
                  <ArrowLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => emblaApi?.scrollNext()}
                  disabled={!canNext}
                  aria-label="Next testimonials"
                  data-testid="carousel-next"
                  className="h-9 w-9 grid place-items-center border border-white/15 text-white transition-colors hover:border-burnt hover:text-burnt disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-white/15 disabled:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt focus-visible:ring-offset-2 focus-visible:ring-offset-ink-surface"
                >
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>

            {/* Viewport */}
            <div
              className="overflow-hidden"
              ref={emblaRef}
              role="region"
              aria-roledescription="carousel"
              aria-label="Testimonials carousel"
            >
              <div className="flex -ml-4 md:-ml-6">
                {rest.map(({ t, i }) => (
                  <div
                    key={t.id}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${t.name}, ${t.relation}`}
                    className="pl-4 md:pl-6 min-w-0 shrink-0 grow-0 basis-[86%] sm:basis-1/2 lg:basis-1/3"
                  >
                    <TestimonialCard t={t} index={i} onOpen={openModal} />
                  </div>
                ))}
              </div>
            </div>

            {/* Progress indicator */}
            <div className="mt-6 flex items-center justify-center gap-2" role="tablist" aria-label="Carousel position">
              {snaps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => emblaApi?.scrollTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  aria-selected={i === selected}
                  role="tab"
                  className={`h-1.5 rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt focus-visible:ring-offset-2 focus-visible:ring-offset-ink-surface ${
                    i === selected ? "w-7 bg-burnt" : "w-1.5 bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>

            {/* View all */}
            <div className="mt-12 flex justify-center">
              <button
                type="button"
                onClick={() => setShowAll(true)}
                data-testid="view-all-testimonials"
                className="group inline-flex items-center gap-2 border border-white/15 text-white font-medium px-7 py-3.5 text-sm transition-colors hover:border-burnt hover:text-burnt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt focus-visible:ring-offset-2 focus-visible:ring-offset-ink-surface"
              >
                <LayoutGrid size={16} className="text-burnt transition-colors group-hover:text-burnt-hover" />
                View All Testimonials
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6" data-testid="testimonials-grid">
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C85A17]">
                All Testimonials ({homeTestimonials.length})
              </p>
              <button
                type="button"
                onClick={() => setShowAll(false)}
                data-testid="collapse-testimonials"
                className="group inline-flex items-center gap-2 text-burnt font-medium text-sm transition-colors hover:text-burnt-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt focus-visible:ring-offset-2 focus-visible:ring-offset-ink-surface"
              >
                <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
                Back to Highlights
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {homeTestimonials.map((t, i) => (
                <Reveal key={t.id} delay={(i % 3) * 0.06} className="h-full">
                  <TestimonialCard t={t} index={i} onOpen={openModal} />
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Full testimonial modal (single, navigable) ── */}
      <Dialog open={modalIndex !== null} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent
          data-lenis-prevent
          data-testid="testimonial-modal"
          className="bg-ink-elevated border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto overscroll-contain"
        >
          {active && (
            <>
              <DialogTitle className="sr-only">
                Testimonial from {active.name}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {active.relation}
              </DialogDescription>

              <Quote className="text-[#C85A17]/40 mt-1" size={40} strokeWidth={1.5} aria-hidden="true" />

              <div className="mt-4 space-y-4">
                {active.full
                  .filter((p) => !p.startsWith("—"))
                  .map((p, i) => (
                    <p key={i} className="text-[#D6D6D6] leading-relaxed">
                      {p}
                    </p>
                  ))}
              </div>

              {/* Attribution — anchored below the testimonial */}
              <div className="mt-8 pt-5 border-t border-white/10">
                <p className="font-heading text-[#F5F3EE] font-semibold">{active.name}</p>
                <p className="mt-1 text-sm font-medium text-[#D9822B]">{active.relation}</p>
              </div>

              {/* Prev / next within the modal */}
              <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={modalPrev}
                  disabled={modalIndex === 0}
                  data-testid="modal-prev"
                  aria-label="Previous testimonial"
                  className="group inline-flex items-center gap-2 text-sm font-medium text-white transition-colors hover:text-burnt disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt focus-visible:ring-offset-2 focus-visible:ring-offset-ink-elevated"
                >
                  <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
                  Previous
                </button>
                <span className="font-mono text-xs text-[#C85A17]">
                  {modalIndex + 1} / {homeTestimonials.length}
                </span>
                <button
                  type="button"
                  onClick={modalNext}
                  disabled={modalIndex === homeTestimonials.length - 1}
                  data-testid="modal-next"
                  aria-label="Next testimonial"
                  className="group inline-flex items-center gap-2 text-sm font-medium text-white transition-colors hover:text-burnt disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt focus-visible:ring-offset-2 focus-visible:ring-offset-ink-elevated"
                >
                  Next
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Testimonials;
